import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import {
  usePlatformAuthenticateLocalUser,
  usePlatformGetCurrentLocalUser,
} from '@cloudquery/api-platform';
import { useBaseAuth, BaseAuthProvider } from '@cloudquery/base-users';
import { useLocation } from 'react-router';
import { SplashScreen, useMount } from 'shared/base';

import { PlatformPermissions } from '@/constants/platformPermissions';

import { getUserTeamsPermissions, getUserPlatformPermissions } from '@/utils/getUserPermissions';

import { AuthContext, AuthContextType } from './context';

interface Props {
  children: ReactNode;
}

function InnerAuthProvider({ children }: Props) {
  const auth = useBaseAuth();
  const abortController = useRef<AbortController | null>(null);
  const { mutateAsync: authenticateUser } = usePlatformAuthenticateLocalUser();
  const {
    data: localUserData,
    isLoading: isLoadingLocalUser,
    refetch: refetchLocalUser,
    remove: removeLocalUser,
  } = usePlatformGetCurrentLocalUser({
    query: {
      enabled: auth.isAuthenticated,
    },
  });

  const signInWithEmailAndPassword = useCallback(
    async (email: string, password: string) => {
      auth.setIsLoading(true);
      auth.setError(null);
      try {
        const {
          data: { id_token: token },
        } = await authenticateUser({ data: { email, password } });
        await auth.signIn(token);
        auth.setProvider('password');
      } catch (error) {
        auth.setIsLoading(false);
        throw error;
      }
    },
    [auth, authenticateUser],
  );

  useMount(() => {
    auth.refresh();

    return () => {
      abortController.current?.abort();
    };
  });

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    if (!isLoadingLocalUser) {
      removeLocalUser();
      refetchLocalUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      refetch: async () => {
        await refetchLocalUser();
      },

      registered:
        auth.isAuthenticated && localUserData?.headers.get('x-cq-activation-valid') === 'true',

      signInWithEmailAndPassword,

      user: localUserData?.data,
      userPlatformPermissions: localUserData
        ? getUserPlatformPermissions(localUserData.data)
        : [PlatformPermissions.GENERAL_READ],
      userTeamsPermissions:
        localUserData && auth.currentMembership
          ? getUserTeamsPermissions(localUserData.data, auth.currentMembership.team)
          : [PlatformPermissions.GENERAL_READ],
    }),
    [
      signInWithEmailAndPassword,
      auth.isAuthenticated,
      auth.currentMembership,
      localUserData,
      refetchLocalUser,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {localUserData || !auth.isAuthenticated ? children : <SplashScreen />}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: Props) {
  const location = useLocation();
  const team = /^\/teams\/([^/]+)/.exec(location.pathname);

  return (
    <BaseAuthProvider pathname={location.pathname} routeQueryTeamName={team?.[1]}>
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </BaseAuthProvider>
  );
}
